<script setup lang="ts">
import { computed, onBeforeUnmount, shallowRef } from 'vue'
import { useTqToast } from '~/shared-kernel/presentation/composables/useTqToast'
import type { EventMapExportLabels, EventMapExportSnapshot, EventMapImageFormat } from './event-map-export.types'
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'

const props = defineProps<{ eventId: string; loadSnapshot: () => Promise<EventMapExportSnapshot> }>()
const { t, locale } = useI18n()
const toast = useTqToast()
const busy = shallowRef(false)
let disposed = false
onBeforeUnmount(() => { disposed = true })

async function exportImage(format: EventMapImageFormat) {
  if (busy.value) return
  busy.value = true
  const eventId = props.eventId
  try {
    const snapshot = await props.loadSnapshot()
    if (disposed || eventId !== props.eventId || snapshot.document.eventId !== eventId) return
    const labels: EventMapExportLabels = {
      title: snapshot.eventName,
      capturedAt: t('eventMap.export.capturedAt', { time: formatBusinessDate(snapshot.capturedAt, locale.value, { dateStyle: 'medium', timeStyle: 'long' }) }),
      legend: t('eventMap.legend.title'),
      localities: t('eventMap.export.localities'),
      snapshotNotice: t('eventMap.export.snapshotNotice'),
      unavailable: t('eventMap.export.unavailable'),
      individualAvailability: t('eventMap.export.individualAvailability'),
      location: t('eventMap.export.location'),
      availableChairs: t('eventMap.export.availableChairs'),
      firstComeFirstServed: t('eventMap.export.firstComeFirstServed')
    }
    const { createEventMapImage, downloadEventMapImage } = await import('./event-map-image.client')
    const blob = await createEventMapImage(snapshot, labels, format)
    if (!disposed && eventId === props.eventId) downloadEventMapImage(blob, snapshot.eventName, snapshot.capturedAt, format)
  } catch {
    if (!disposed) toast.error({ title: t('eventMap.export.errorTitle'), message: t('eventMap.export.errorDescription') })
  } finally {
    busy.value = false
  }
}
const items = computed(() => [
  { label: t('eventMap.export.png'), icon: 'i-lucide-image-down', onSelect: () => exportImage('png') },
  { label: t('eventMap.export.jpeg'), icon: 'i-lucide-image-down', onSelect: () => exportImage('jpeg') }
])
</script>

<template>
  <UDropdownMenu :items="items">
    <TqButton compact variant="secondary" :loading="busy" :disabled="busy" :aria-busy="busy">
      <UIcon name="i-lucide-download" aria-hidden="true" />
      {{ t('eventMap.export.button') }}
    </TqButton>
  </UDropdownMenu>
</template>
